import { Spinner } from '@epam/promo';
import { useAdminGetCurrent } from 'hooks/api/useAdminGetCurrent';
import { REVOKED_STATUS, UserRole } from 'models/user.model';
import {
  Navigate,
  Outlet,
  useLocation,
  useOutlet,
  matchRoutes,
  Location,
} from 'react-router-dom';
import routes from 'router';
import { useApplicationStore } from 'store/application';
import { findFallbackSiblingPath } from 'hooks/helpers';

interface LocationStateProps {
  visitedPaths?: string[];
}

export const RequireUser = ({ allowedRoles }: { allowedRoles: UserRole[] }) => {
  const location: Location<LocationStateProps> = useLocation();
  const outlet = useOutlet();

  const { user, isLoading: isAdminLoading } = useAdminGetCurrent();
  const { isLoading } = useApplicationStore((state) => state);

  if (isAdminLoading) {
    return <Spinner cx="spinner" />;
  }

  if (!user) {
    return <Navigate replace state={{ from: location }} to="/login" />;
  }

  if (!outlet) {
    return <Navigate replace to="/not-found" />;
  }

  const hasAccess =
    allowedRoles.includes(user.role) && user.status !== REVOKED_STATUS;

  if (hasAccess) {
    return (
      <>
        {isLoading && <Spinner cx="spinner" />}
        <Outlet />
      </>
    );
  }

  const matches = matchRoutes(routes, location.pathname);

  const visitedPaths = [
    ...(location.state?.visitedPaths ?? []),
    location.pathname,
  ];

  const fallbackPath = findFallbackSiblingPath(matches, visitedPaths);

  if (fallbackPath) {
    return (
      <Navigate replace state={{ visitedPaths }} to={`../${fallbackPath}`} />
    );
  }

  return <Navigate replace state={{ from: location }} to="/forbidden" />;
};
